import { useState, useEffect } from 'react';
import { apiUrl } from '../utils/apiBase';
import { useAuth } from '../contexts/AuthContext';
import QRLogin from './QRLogin';
import { QrCode, LogIn, X } from 'lucide-react';

export default function LoginPage() {
  const { login, isLoading } = useAuth();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);

  // Gestione escape key per chiudere il modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showQRModal) {
        setShowQRModal(false);
      }
    };
    
    if (showQRModal) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showQRModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!credentials.username || !credentials.password) {
      setError('Username e password sono richiesti');
      return;
    }
    
    const result = await login(credentials.username, credentials.password);
    if (!result.success) {
      setError(result.error);
    }
  };

  const handleInputChange = (field, value) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // Pulisci errore quando l'utente inizia a digitare
  };

  const handleQRSuccess = async (token, user) => {
    console.log('🎉 QR Login Success:', { token: token ? 'Present' : 'Missing', user });
    
    // Chiudi il modal QR
    setShowQRModal(false);
    
    // Salva il token nel localStorage
    localStorage.setItem('ktv-token', token);
    
    // Forza il re-init del sistema di autenticazione
    // Invece di reload, triggeriamo manualmente il sistema di auth
    try {
      // Verifica che il token sia valido chiamando l'endpoint di verifica
      const response = await fetch(apiUrl('/api/auth/verify'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        console.log('✅ Token QR verificato, ricaricamento app...');
        window.location.reload(); // Trigger re-auth nel AuthContext
      } else {
        console.error('❌ Token QR non valido');
        setError('Token di autenticazione non valido');
      }
    } catch (error) {
      console.error('❌ Errore verifica token QR:', error);
      setError('Errore durante la verifica del token');
    }
  };

  const handleQRError = (errorMessage) => {
    setError(errorMessage);
  };

  const renderTraditionalLogin = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Username field */}
      <div>
        <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
          Username
        </label>
        <input
          id="username"
          type="text"
          value={credentials.username}
          onChange={(e) => handleInputChange('username', e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200"
          placeholder="Inserisci il tuo username"
          required
          autoComplete="username"
          disabled={isLoading}
        />
      </div>
      
      {/* Password field */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
          Password
        </label>
        <input
          id="password"
          type="password"
          value={credentials.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200"
          placeholder="Inserisci la tua password"
          required
          autoComplete="current-password"
          disabled={isLoading}
        />
      </div>
      
      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-500/20 border border-red-400/50 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}
      
      {/* Submit buttons */}
      <div className="flex gap-3">
        {/* Pulsante Accedi principale */}
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500/50"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Accesso in corso...
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <LogIn size={18} />
              Accedi
            </div>
          )}
        </button>
        
        {/* Pulsante QR Code */}
        <button
          type="button"
          onClick={() => setShowQRModal(true)}
          disabled={isLoading}
          className="px-4 py-3 bg-gray-800/70 hover:bg-gray-700/70 border border-gray-600/50 hover:border-gray-500/50 text-gray-300 hover:text-white rounded-lg transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-500/50 group"
          title="Login con QR Code"
        >
          <QrCode size={18} className="group-hover:text-blue-400 transition-colors" />
        </button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header con logo e titolo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img 
              src={typeof window !== 'undefined' && window.ktvEnv && window.ktvEnv.isElectron
                ? new URL('../../public/kainolab_logo.png', import.meta.url).href
                : '/kainolab_logo.png'}
              alt="KAINO-TV Logo" 
              className="w-12 h-12 object-contain rounded-lg"
            />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              kainoTV
            </h1>
          </div>
          <p className="text-gray-400">Accedi per continuare</p>
        </div>

        {/* Form container */}
        <div className="bg-gray-950/90 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-8 shadow-2xl">
          {renderTraditionalLogin()}
        </div>

        {/* Footer info */}
        <div className="text-center mt-6">
          <p className="text-gray-500 text-sm">
            Streaming multimediale privato • Sicuro e protetto
          </p>
        </div>
      </div>

      {/* QR Modal */}
      {showQRModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            // Chiudi il modal solo se si clicca sull'overlay, non sul contenuto
            if (e.target === e.currentTarget) {
              setShowQRModal(false);
            }
          }}
        >
          <div className="relative w-full max-w-md">
            {/* QR Login Component */}
            <div className="bg-gray-950/95 backdrop-blur-sm border border-gray-700/50 rounded-2xl shadow-2xl">
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-800/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600/20 rounded-lg">
                    <QrCode className="text-blue-400" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Login QR Code</h3>
                    <p className="text-sm text-gray-400">Autenticazione tramite smartphone</p>
                  </div>
                </div>
              </div>
              
              {/* Modal Content */}
              <div className="p-6">
                <QRLogin 
                  onSuccess={handleQRSuccess} 
                  onError={handleQRError}
                  onClose={() => setShowQRModal(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
